/**
 * API client configuration for SMETA AI.
 */
import axios, { type AxiosResponse, type InternalAxiosRequestConfig } from 'axios';
import { toast } from 'sonner';
import { findAccount, getActiveAccount, removeAccount, updateAccountProfile } from '@/lib/accounts';
import { clearApiFileCache } from '@/lib/apiFileCache';
import { loginPathFor } from '@/lib/mobileApp';
import { notifyApiNotFound } from '@/lib/apiNotFound';
import { isAuthRequest, refreshAccessToken } from './authRefresh';

import { API_BASE_URL } from './baseUrl';

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 120000,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

/**
 * KAN-834: запрос уже повторён после обновления токена (второй раз не обновляем).
 * KAN-925: аккаунт, с токеном которого ушёл запрос: refresh и повтор идут им же,
 * даже если вкладка тем временем переключилась на другой аккаунт.
 */
type AccountConfig = InternalAxiosRequestConfig & { _authRetry?: boolean; _accountId?: string };

// Request interceptor
apiClient.interceptors.request.use(
  (config: AccountConfig) => {
    // KAN-834: вход, обмен ссылки и refresh идут без Authorization: старый токен
    // давал 401 token_not_valid до входа. KAN-925: токен активного аккаунта вкладки.
    if (isAuthRequest(config.url)) return config;
    const account = config._accountId ? findAccount(config._accountId) : getActiveAccount();
    if (account) {
      config._accountId = account.userId;
      if (!config._authRetry) config.headers.Authorization = `Bearer ${account.access}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

/**
 * KAN-925: refresh аккаунта не вышел: уходит только этот аккаунт. Если он был
 * активным во вкладке, переход на вход (не тихое переключение на другой).
 */
function signOutExpiredAccount(accountId: string): void {
  const wasActive = getActiveAccount()?.userId === accountId;
  removeAccount(accountId);
  if (!wasActive) return;
  clearApiFileCache();
  // KAN-834: из приложения менеджера (/m) на страницу "Попросите новую ссылку".
  window.location.href = loginPathFor(window.location.pathname);
}

// Response interceptor
/**
 * KAN-925: ответ /auth/me/ дописывает профиль (имя, роль, кабинет) в аккаунт,
 * отправивший запрос. Одно место для всех queryFn ключа ['currentUser'].
 */
const AUTH_ME_URL = /\/auth\/me\/?$/;

function rememberProfile(response: AxiosResponse): void {
  const config = response.config as AccountConfig;
  const user = response.data as { id?: unknown } | null;
  if (!config._accountId || !AUTH_ME_URL.test(config.url ?? '')) return;
  if (!user || typeof user.id !== 'string') return;
  updateAccountProfile(config._accountId, { ...(user as object), id: user.id });
}

apiClient.interceptors.response.use(
  (response) => {
    rememberProfile(response);
    return response;
  },
  async (error) => {
    if (error.response?.status === 401) {
      const config = error.config as AccountConfig | undefined;
      const accountId = config?._accountId;
      // 401 на входе или без аккаунта: ошибку показывает форма, вошедшие не трогаются.
      if (config && accountId && !isAuthRequest(config.url)) {
        const account = findAccount(accountId);
        if (account && !config._authRetry) {
          // KAN-834: истёкший access обновляется по refresh один раз, запрос повторяется.
          // Пока запрос шёл, токен уже обновили (соседний запрос или вкладка): повтор без refresh.
          const used = String(config.headers?.Authorization ?? '');
          const access =
            used !== `Bearer ${account.access}`
              ? account.access
              : await refreshAccessToken(account.userId, API_BASE_URL);
          if (access) {
            config._authRetry = true;
            config.headers.Authorization = `Bearer ${access}`;
            return apiClient(config);
          }
        }
        if (findAccount(accountId)) signOutExpiredAccount(accountId);
      }
    }
    // KAN-944: 404 любого запроса - сигнал слушателям (например, карточка клиента
    // перепроверяет, не взял ли клиента другой менеджер).
    if (error.response?.status === 404) {
      notifyApiNotFound(String(error.config?.url ?? ''));
    }
    // KAN-341: 402 — «нужен действующий тариф». Показываем текст с бэкенда,
    // а не свой: причина там одна (нет тарифа / истёк / отменён), и вторая
    // формулировка здесь однажды разошлась бы с первой.
    if (error.response?.status === 402) {
      const detail =
        error.response?.data?.detail ||
        'Нужен действующий тариф. Продлите его в разделе «Биллинг».';
      toast.error(detail, {
        action: {
          label: 'К тарифам',
          onClick: () => {
            window.location.href = '/billing';
          },
        },
      });
    }
    return Promise.reject(error);
  }
);

export default apiClient;
